/**
 * API client for community role slots — Geo Community team-member roster.
 *
 * POLICY_TEAM_ROSTERS.md §4. Migration 170 retired the hardcoded
 * role_type enum, the is_core flag, and the role_label override column
 * in favor of a single freeform display_title field.
 *
 * Permission semantics: roster-only. These rows do NOT grant platform
 * permissions; ACL is owned by V2 permission_grants (Audience &
 * Permissions tab). A future read-side enrichment can auto-compute a
 * badge by joining linked_user_guid to active V2 grants for the same
 * community scope.
 */

import { apiFetch } from "./client";
import type { CommunityEnabler } from "../Data/CommunityConfig/communityEnablers";

/* ------------------------------------------------------------------ */
/*  Response type (mirrors CommunityRoleSlotResponse DTO)              */
/* ------------------------------------------------------------------ */

export interface RoleSlotResponse {
  roleSlotGuid: string;
  communityEntityGuid: string;
  displayTitle: string;
  sortOrder: number;
  /** Migration 171 — when true, hide from public Key Community Leaders. */
  isHidden: boolean;
  linkedUserGuid: string | null;
  manualName: string | null;
  manualTitle: string | null;
  manualPhotoUrl: string | null;
  manualEmail: string | null;
  manualPhone: string | null;
  externalProfileUrl: string | null;
  notes: string | null;
  /** DEV-1159 Phase A — per-slot role-definition fields (denormalized). */
  description: string | null;
  skillsRequired: string[];
  timeCommitment: string | null;
  /** DEV-1121 / migration 192. Per-slot permission bundle (V2 grant cascade). */
  permissionSetGuid: string | null;
  /** DEV-1157 Phase 2d. Optional Tier-1 catalog linkage. */
  sourceDefinitionGuid: string | null;
  /** DEV-1193 Phase 3. Summary of the slot's open invitation/assignment
   * row (status IN 'pending_invitation' or 'active'), if any. NULL when
   * the slot has no open assignment (vacant or legacy direct-write). */
  openAssignment: OpenAssignmentSummary | null;
  createdAt: string;
  updatedAt: string;
  /** DEV-1195 Phase 1. Soft-delete timestamp. Populated only on the
   * `/deleted` listing endpoint; NULL on active reads. */
  deletedAt: string | null;
  /** DEV-1195 Phase 1. User who initiated the soft-delete. Populated
   * only on the `/deleted` listing endpoint. Forensic only. */
  deletedByUserGuid: string | null;
}

/** DEV-1193 Phase 3. Lightweight summary surfaced inline on the slot
 * read response. Enough info for the slot card to render a "Pending
 * invitation — [Name]" badge and for admin actions (revoke). */
export interface OpenAssignmentSummary {
  assignmentGuid: string;
  /** One of: pending_invitation, active. */
  status: string;
  userGuid: string;
  userDisplayName: string | null;
  invitedAt: string;
  invitedByUserGuid: string;
  invitedByDisplayName: string | null;
}

/* ------------------------------------------------------------------ */
/*  Request type (mirrors UpsertRoleSlotRequest DTO)                   */
/* ------------------------------------------------------------------ */

export interface UpsertRoleSlotPayload {
  roleSlotGuid?: string | null;
  displayTitle: string;
  sortOrder: number;
  /** Migration 171 — when true, hide from public Key Community Leaders. */
  isHidden: boolean;
  linkedUserGuid?: string | null;
  manualName?: string | null;
  manualTitle?: string | null;
  manualPhotoUrl?: string | null;
  manualEmail?: string | null;
  manualPhone?: string | null;
  externalProfileUrl?: string | null;
  notes?: string | null;
  /** DEV-1159 Phase A — per-slot role-definition fields. */
  description?: string | null;
  /** Undefined leaves unchanged; [] clears. */
  skillsRequired?: string[];
  timeCommitment?: string | null;
  /** DEV-1121 / migration 192. Empty-GUID string ("00000000-…") clears; non-empty sets; undefined leaves unchanged. */
  permissionSetGuid?: string | null;
  /** DEV-1157 Phase 2d. Optional Tier-1 catalog linkage. NULL clears. */
  sourceDefinitionGuid?: string | null;
}

/* ------------------------------------------------------------------ */
/*  Shape conversion: API response → in-memory CommunityEnabler        */
/* ------------------------------------------------------------------ */

export function roleSlotResponseToEnabler(r: RoleSlotResponse): CommunityEnabler {
  return {
    role_slot_guid: r.roleSlotGuid,
    community_entity_guid: r.communityEntityGuid,
    display_title: r.displayTitle,
    sort_order: r.sortOrder,
    is_hidden: r.isHidden,
    linked_user_guid: r.linkedUserGuid,
    manual_name: r.manualName ?? undefined,
    manual_title: r.manualTitle ?? undefined,
    manual_photo_url: r.manualPhotoUrl ?? undefined,
    manual_email: r.manualEmail ?? undefined,
    manual_phone: r.manualPhone ?? undefined,
    external_profile_url: r.externalProfileUrl ?? undefined,
    notes: r.notes ?? undefined,
    // DEV-1159 Phase A — pass through if the consumer types add these.
    // CommunityEnabler shape doesn't currently surface them; the unified
    // Role tray reads from RoleSlotResponse directly.
    source_definition_guid: r.sourceDefinitionGuid, // DEV-1157 Phase 2d
    created_at: r.createdAt,
    updated_at: r.updatedAt,
  };
}

/** Convert in-memory enabler → API upsert payload */
export function enablerToUpsertPayload(e: CommunityEnabler): UpsertRoleSlotPayload {
  return {
    roleSlotGuid: e.role_slot_guid,
    displayTitle: e.display_title,
    sortOrder: e.sort_order,
    isHidden: e.is_hidden ?? false,
    linkedUserGuid: e.linked_user_guid || null,
    manualName: e.manual_name ?? null,
    manualTitle: e.manual_title ?? null,
    manualPhotoUrl: e.manual_photo_url ?? null,
    manualEmail: e.manual_email ?? null,
    manualPhone: e.manual_phone ?? null,
    externalProfileUrl: e.external_profile_url ?? null,
    notes: e.notes ?? null,
    sourceDefinitionGuid: e.source_definition_guid ?? null, // DEV-1157 Phase 2d
  };
}

/* ------------------------------------------------------------------ */
/*  API functions                                                       */
/* ------------------------------------------------------------------ */

const BASE = "/api/CommunityRoleSlots";

/** Fetch all role slots for a community */
export async function fetchRoleSlots(communityGuid: string): Promise<RoleSlotResponse[]> {
  return apiFetch<RoleSlotResponse[]>(
    `${BASE}?communityGuid=${encodeURIComponent(communityGuid)}`
  );
}

/** Upsert a single role slot */
export async function upsertRoleSlot(
  roleSlotGuid: string,
  communityGuid: string,
  payload: UpsertRoleSlotPayload
): Promise<RoleSlotResponse> {
  return apiFetch<RoleSlotResponse>(
    `${BASE}/${roleSlotGuid}?communityGuid=${encodeURIComponent(communityGuid)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );
}

/** Bulk save — replace all role slots for a community */
export async function saveAllRoleSlots(
  communityGuid: string,
  slots: UpsertRoleSlotPayload[]
): Promise<RoleSlotResponse[]> {
  return apiFetch<RoleSlotResponse[]>(
    `${BASE}/bulk?communityGuid=${encodeURIComponent(communityGuid)}`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slots }),
    }
  );
}

/**
 * Soft-delete a single role slot (DEV-1195).
 *
 * Sends DELETE /api/CommunityRoleSlots/{guid}. The backend sets
 * deleted_at + deleted_by_user_guid and revokes the slot-anchored V2
 * grants. The slot is reversibly trashed for 30 days; after that the
 * background sweep hard-purges it.
 *
 * Default Lead / Observer slots return 400 (default_slot_not_deletable).
 * The caller should surface the error message to the user.
 */
export async function deleteRoleSlot(roleSlotGuid: string): Promise<void> {
  await apiFetch<void>(`${BASE}/${roleSlotGuid}`, { method: "DELETE" });
}

/**
 * Restore a soft-deleted role slot (DEV-1195 Phase 1).
 *
 * Reverses a prior DELETE: clears deleted_at + deleted_by_user_guid and
 * re-issues the slot-anchored V2 grant via the backend's
 * ReconcileSlotRoleGrant. Returns 404 if the slot wasn't soft-deleted.
 */
export async function restoreRoleSlot(roleSlotGuid: string): Promise<void> {
  await apiFetch<void>(`${BASE}/${roleSlotGuid}/restore`, { method: "POST" });
}

/**
 * Hard-purge a soft-deleted role slot (DEV-1195 Phase 1, sysadmin only).
 *
 * Permanently destroys the slot row, cascading
 * community_role_slot_assignments. Used by the Empty-Trash flow and by
 * the 30-day background sweep internally. Returns 403 for non-sysadmin
 * callers.
 */
export async function purgeRoleSlot(roleSlotGuid: string): Promise<void> {
  await apiFetch<void>(`${BASE}/${roleSlotGuid}/purge`, { method: "DELETE" });
}

/**
 * Fetch soft-deleted slots for a community (DEV-1195 Phase 1).
 *
 * Drives the "Recently deleted" subsection in UnifiedTeamTab. Sorted by
 * deletedAt DESC (most-recently-trashed first). Responses include the
 * deletedAt + deletedByUserGuid fields, which are NULL on active reads.
 */
export async function fetchDeletedRoleSlots(
  communityGuid: string,
): Promise<RoleSlotResponse[]> {
  return apiFetch<RoleSlotResponse[]>(
    `${BASE}/deleted?communityGuid=${encodeURIComponent(communityGuid)}`,
  );
}

/* ------------------------------------------------------------------ */
/*  DEV-1145 Phase 1j — per-user slot occupancies                       */
/*                                                                      */
/*  Flat per-user view returned by GET /api/communityroleslots/by-user. */
/*  Consumed by the Engagements page to surface slot-based roles        */
/*  (Club Secretary, Mayor, etc.) under the Community pillar.           */
/* ------------------------------------------------------------------ */

export interface CommunitySlotOccupancyResponse {
  roleSlotGuid: string;
  communityEntityGuid: string;
  communityName: string;
  /** community_entities.entity_type — geo_country / geo_state / geo_city /
   *  org_rotary_club / org_school / org_business / etc. */
  entityType: string;
  /** Slot label, e.g. "Club Secretary", "Mayor", "Town Manager". */
  displayTitle: string;
  /** TRUE = Lead/Officer/Observer default slot (migration 192).
   *  FALSE = admin-defined leader-figurehead slot. */
  isDefaultSlot: boolean;
  isHidden: boolean;
  /** Tier-1 role_definitions linkage — drives Role Rhythm attach. */
  sourceDefinitionGuid: string | null;
  /** DEV-1193 follow-up: active community_role_slot_assignments.assignment_guid
   *  for this (slot, user) pair, when the occupancy came from the new
   *  invitation flow. NULL when the slot was filled via the legacy
   *  direct-write path (pre-DEV-1193). Used by the Engagements page
   *  Resign action to route to /api/community-role-slot-invitations/{guid}/resign. */
  assignmentGuid: string | null;
  createdAt: string;
  updatedAt: string;
}

/** Fetch every community slot the given user occupies. */
export async function fetchCommunitySlotOccupancies(
  userGuid: string,
): Promise<CommunitySlotOccupancyResponse[]> {
  return apiFetch<CommunitySlotOccupancyResponse[]>(
    `${BASE}/by-user/${userGuid}`,
  );
}

/* ------------------------------------------------------------------ */
/*  DEV-1368 — per-user PREVIOUS community roles                        */
/*                                                                      */
/*  Terminal community_role_slot_assignments (revoked / removed) whose  */
/*  slot + community still exist. Surfaced in the Action Center rail    */
/*  behind the "Show previous roles" toggle, rendered muted with a      */
/*  "Former" pill. Parallel to the project-previous-roles path.         */
/* ------------------------------------------------------------------ */

export interface CommunityPreviousRoleResponse {
  roleSlotGuid: string;
  communityEntityGuid: string;
  communityName: string;
  entityType: string;
  displayTitle: string;
  isDefaultSlot: boolean;
  isHidden: boolean;
  sourceDefinitionGuid: string | null;
  /** Terminal lifecycle status: "revoked" or "removed". */
  status: string;
  /** When the assignment ended (terminal transition timestamp). */
  endedAt: string | null;
  /** Optional explanation captured at resign/revoke/remove time. */
  endReason: string | null;
}

/** Fetch the community/organization roles the given user previously held. */
export async function fetchCommunityPreviousRoles(
  userGuid: string,
): Promise<CommunityPreviousRoleResponse[]> {
  return apiFetch<CommunityPreviousRoleResponse[]>(
    `${BASE}/by-user/${userGuid}/previous`,
  );
}
